import React from "react";
import { scroller, Events } from "react-scroll";
import { Edition } from "../enums/Edition";
import { Chapter } from "../types/Chapter";
import { VerseModel } from "../types/VerseModel";
import { VerseLocationForText } from "../types/VerseLocationForText";
import { toAllah } from "../utils/ToAllah";
import { appBarColor, topAppBarHeight, defaultQuranFontSize } from "../constants/BaseConstants";
import GodAllahSwitch from "./GodAllahSwitch";
import { Box, Typography } from "@mui/material";

const verticalOffset = 5;

const header = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  marginTop: "15px",
  marginBottom: "7px",
} as const;

const headerLeft = {} as const;
const headerRight = {} as const;

// Titles Main Page ------------------------------------------------
const titleTop_mainPage = {
  fontWeight: "500",
  lineHeight: "1.0em",
  fontVariant: "small-caps" as const,
  color: "#222",
  fontSize: "28px",
  marginLeft: "-2px",
  letterSpacing: "0.0rem",
} as const;

const titleTopMiddle_mainPage = {
  fontWeight: "400",
  letterSpacing: "-0.05rem",
  color: "#333",
  fontSize: "25px",
} as const;

const titleTopRight_mainPage = {
  fontWeight: "400",
  letterSpacing: "-0.05rem",
  color: "#444",
  fontSize: "25px",
} as const;

const titleBottom_mainPage = {
  fontWeight: "300",
  marginTop: "5px",
  color: "#000",
  fontSize: "22.0px",
} as const;

// Titles Chapters Page ------------------------------------------------
const titleTop_chaptersPage = {
  fontWeight: "500",
  lineHeight: "1.0em",
  color: "#222",
  fontSize: "24px",
  marginLeft: "-2px",
  letterSpacing: "0.0rem",
} as const;

const titleBottom_chaptersPage = {
  fontWeight: "300",
  lineHeight: "1.2em",
  color: "#000",
  fontSize: "30px",
  marginLeft: "-2px",
  letterSpacing: "0.0rem",
} as const;

const titleBottomRight_chaptersPage = { fontSize: "23px" } as const;

//------------------------------------------------------------
const verseBase = {
  paddingTop: "0.35em",
  paddingBottom: "0.35em",
  marginTop: "0px",
  marginBottom: "0px",
  color: "#000",
  lineHeight: "1.35em",
  cursor: "pointer",
} as const;

const verseSelected = {
  borderTop: "1px solid #ffecb3",
  borderBottom: "1px solid #ffecb3",
  backgroundColor: "#fffde7",
} as const;

const verseNotSelected = {
  borderTop: "1px solid #ffffff",
  borderBottom: "1px solid #ffffff",
  backgroundColor: "#ffffff",
} as const;

const verseNumber = {
  color: appBarColor,
  fontSize: "85%",
} as const;

const spacer = { height: "15px" } as const;

interface QuranTextProps {
  chapter: Chapter;
  edition: Edition;
  verseLocation: VerseLocationForText;
  fontScaling: number;
  chapterRenderingDone: () => void;
  verseScrollingDone: (verseNumber: number) => void;
  verseClicked: (verseNumber: number) => void;
  handleEditionChange: (edition: Edition) => void;
  contentVisibilityChanged: (isVisible: boolean) => void;
}

// tiny memoized row — only re-renders when its inputs change
const VerseRow = React.memo(function VerseRow({
  verse,
  isSelected,
  fontSizeStyle,
  onClick,
  text,
}: {
  verse: VerseModel;
  isSelected: boolean;
  fontSizeStyle: React.CSSProperties;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  text: string;
}) {
  return (
    <Typography
      sx={[verseBase, isSelected ? verseSelected : verseNotSelected]}
      style={fontSizeStyle}
      onClick={onClick}
      id={"verse" + verse.verseNumber}
      data-vn={verse.verseNumber}
    >
      {verse.verseNumber !== 0 && (
        <Box component="span" sx={verseNumber}>
          {" "}
          {verse.verseNumber}.&nbsp;
        </Box>
      )}
      {text}
    </Typography>
  );
}, (a, b) => {
  // re-render when selection changes, the text changes, or font size object identity changes
  return (
    a.verse.verseNumber === b.verse.verseNumber &&
    a.isSelected === b.isSelected &&
    a.text === b.text &&
    a.fontSizeStyle === b.fontSizeStyle &&
    a.onClick === b.onClick
  );
});

function QuranTextImpl(props: QuranTextProps): JSX.Element {
  const {
    chapter,
    edition,
    verseLocation,
    fontScaling,
    chapterRenderingDone,
    verseScrollingDone,
    verseClicked,
    contentVisibilityChanged,
  } = props;

  // Single click handler — reads verse number from data attribute (no per-row closures)
  const handleVerseClicked = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const vn = Number((event.currentTarget as HTMLElement).dataset.vn);
      if (!Number.isNaN(vn)) verseClicked(vn);
    },
    [verseClicked],
  );

  // Register react-scroll "end" once
  React.useEffect(() => {
    const handler = (to: string) => {
      if (to?.startsWith?.("verse")) {
        verseScrollingDone(+to.replace("verse", ""));
      }
    };
    Events.scrollEvent.register("end", handler);
    return () => {
      Events.scrollEvent.remove("end");
    };
  }, [verseScrollingDone]);

  const fontSizeStyle = React.useMemo<React.CSSProperties>(
    () => ({ fontSize: (fontScaling * defaultQuranFontSize) / 100 + "rem" }),
    [fontScaling],
  );

  // Precompute verse texts for current edition so we don't run toAllah on every row every render
  const verseTexts = React.useMemo(() => {
    if (edition === Edition.God) return chapter.verses.map(v => v.verseString);
    return chapter.verses.map(v => toAllah(v.verseString));
  }, [chapter.verses, edition]);

  // helpers
  function isElementInViewport(element: HTMLElement | null, yOffset: number) {
    try {
      if (!element) return true;
      const rect = element.getBoundingClientRect();
      return rect.top - yOffset >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
    } catch {
      return true;
    }
  }

  function isElementScrolledUp(element: HTMLElement | null, yOffset: number) {
    try {
      if (!element) return true;
      const rect = element.getBoundingClientRect();
      return rect.top - yOffset >= 0;
    } catch {
      return true;
    }
  }

  // Scroll to target verse when requested
  React.useEffect(() => {
    const itemVerse = document.getElementById("verse" + verseLocation.verseNumber);
    const isVerseInViewPort = isElementInViewport(itemVerse, +topAppBarHeight - verticalOffset);
    const itemTitles = document.getElementById("chapter-titles");
    const isTitlesScrolledUp = isElementScrolledUp(itemTitles, +topAppBarHeight - verticalOffset);

    if (verseLocation.isScrollToVerse) {
      if (!document.hidden) {
        if (verseLocation.verseNumber === 0 || verseLocation.verseNumber === 1) {
          // special-case: top of chapter
          if (!isTitlesScrolledUp) {
            const option = {
              smooth: verseLocation.isAnimateScroll ? true : false,
              offset: -topAppBarHeight - 300, // force all the way up
            };
            scroller.scrollTo("verse" + verseLocation.verseNumber, option);
          } else {
            verseScrollingDone(verseLocation.verseNumber);
          }
        } else if (!isVerseInViewPort) {
          const option = {
            smooth: verseLocation.isAnimateScroll ? true : false,
            offset: -topAppBarHeight - verticalOffset,
          };
          scroller.scrollTo("verse" + verseLocation.verseNumber, option);
        } else {
          verseScrollingDone(verseLocation.verseNumber);
        }
      } else {
        // document is hidden (e.g., tab switch) → mark as done without scrolling
        verseScrollingDone(verseLocation.verseNumber);
      }
    } else {
      // no scrolling requested → still notify current verse
      verseScrollingDone(verseLocation.verseNumber);
    }

    // Make content visible again after any managed scroll
    contentVisibilityChanged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  verseLocation
]);

  // Notify when chapter's initial render completes
  React.useEffect(() => {
    chapterRenderingDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter.chapterNumber]);

  return (
    <Box>
      {/* titles block */}
      <Box sx={header} id="chapter-titles">
        {/* Titles if main page */}
        {chapter.chapterNumber === 1 && (
          <Box sx={headerLeft}>
            <Typography sx={titleTop_mainPage} variant="h1">
              Quran
              <Box component="span" sx={titleTopMiddle_mainPage}>
                &nbsp;in&nbsp;
              </Box>
              <Box component="span" sx={titleTopRight_mainPage}>
                english
              </Box>
            </Typography>
            <Typography sx={titleBottom_mainPage} variant="h2">
              Clear&#8201;&bull;&#8201;Pure&#8201;&bull;&#8201;Precise
            </Typography>
          </Box>
        )}

        {/* Titles if chapters pages */}
        {chapter.chapterNumber !== 1 && (
          <Box sx={headerLeft}>
            <Typography sx={titleTop_chaptersPage} variant="h1">
              Surah {chapter.nameTrans}
            </Typography>
            <Typography sx={titleBottom_chaptersPage} variant="h2">
              Quran
              <Box sx={titleBottomRight_chaptersPage} component="span">
                {" "}
                Chapter {chapter.chapterNumber}
              </Box>
            </Typography>
          </Box>
        )}

        <Box sx={headerLeft}>
          <Box sx={headerRight}>
            {/* pass handler directly (stable), no inline arrow */}
            <GodAllahSwitch initialSelectedIndex={edition} handleEditionChange={props.handleEditionChange} />
          </Box>
        </Box>
      </Box>

      <Box sx={spacer} />

      {/* verses block */}
      {chapter.verses.map((verse, idx) => (
        <VerseRow
          key={verse.verseNumber}
          verse={verse}
          isSelected={verseLocation.verseNumber === verse.verseNumber}
          fontSizeStyle={fontSizeStyle}
          onClick={handleVerseClicked}
          text={verseTexts[idx]}
        />
      ))}
    </Box>
  );
}

// Avoid re-renders unless the props that materially affect output change
export default React.memo(QuranTextImpl, (a, b) => {
  if (a.edition !== b.edition) return false;
  if (a.fontScaling !== b.fontScaling) return false;

  if (a.verseLocation.verseNumber !== b.verseLocation.verseNumber) return false;
  if (a.verseLocation.isScrollToVerse !== b.verseLocation.isScrollToVerse) return false;
  if (a.verseLocation.isAnimateScroll !== b.verseLocation.isAnimateScroll) return false;

  const aTick =
    (a.verseLocation as any).isForceNewVerseNumber ??
    (a.verseLocation as any).forceNewVerseNumber ??
    (a.verseLocation as any).force ??
    0;
  const bTick =
    (b.verseLocation as any).isForceNewVerseNumber ??
    (b.verseLocation as any).forceNewVerseNumber ??
    (b.verseLocation as any).force ??
    0;
  if (aTick !== bTick) return false;

  if (a.chapter.chapterNumber !== b.chapter.chapterNumber) return false;
  if (a.chapter.nameTrans !== b.chapter.nameTrans) return false;
  if (a.chapter.verses !== b.chapter.verses) return false;

  return true;
});


